import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { Terrain } from '../game/Terrain';
import { readTune } from '../ui/TuneStore';

import { assetUrl as _assetUrl } from '../ui/AssetMap';
const MODEL_SLOT = 'magnifying-glass';
const MODEL_URL_DEFAULT = '/assets/magnifying-glass.glb';
const TUNE_ID = 'magnifying-glass';
const TUNE_DEFAULTS = { scale: 0.5, rotX: 0, rotY: 0, rotZ: 0, offY: 0 };
const HOVER_HEIGHT = 0.6;   // metres above the terrain hit point

export interface MagGlassControl {
  readonly group: THREE.Group;
  /** Attach input + toggle listeners. Also handles M key toggle. */
  attach(dom: HTMLCanvasElement, camera: THREE.PerspectiveCamera): void;
  dispose(): void;
  /** Call each frame. Applies damage + scatter when active. */
  update(
    dt: number,
    damage: (x: number, z: number, radius: number, dps: number, dt: number) => number,
    scatter: (x: number, z: number, radius: number, force?: number) => void,
  ): void;
  readonly isActive: boolean;
}

/**
 * SimAnt-style magnifying-glass hazard. Toggled with the M key; while active,
 * the cursor emits a bright disc of focused sunlight that burns any ants it
 * touches and scatters nearby survivors. Inactive state hides the visuals and
 * lets OrbitControls / other input handlers run undisturbed.
 */
export class MagnifyingGlass implements MagGlassControl {
  readonly group: THREE.Group;

  private readonly terrain: Terrain;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();

  private dom: HTMLCanvasElement | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private prevCursor = '';

  private active = false;
  private hasCursor = false;
  private hitX = 0;
  private hitZ = 0;
  private hitY = 0;

  private readonly outerDisc: THREE.Mesh;
  private readonly innerDisc: THREE.Mesh;
  private modelRoot: THREE.Group | null = null;

  constructor(terrain: Terrain) {
    this.terrain = terrain;

    this.group = new THREE.Group();
    this.group.name = 'mag-glass';
    this.group.visible = false;

    const outerGeo = new THREE.CircleGeometry(0.8, 32);
    outerGeo.rotateX(-Math.PI / 2);
    const outerMat = new THREE.MeshBasicMaterial({
      color: 0xfff1a0,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.outerDisc = new THREE.Mesh(outerGeo, outerMat);
    this.outerDisc.renderOrder = 2;
    this.group.add(this.outerDisc);

    const innerGeo = new THREE.CircleGeometry(0.3, 32);
    innerGeo.rotateX(-Math.PI / 2);
    const innerMat = new THREE.MeshBasicMaterial({
      color: 0xfffffa,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.innerDisc = new THREE.Mesh(innerGeo, innerMat);
    this.innerDisc.renderOrder = 2;
    this.group.add(this.innerDisc);

    void this.loadModel();
  }

  /** Async-load the magnifying-glass GLB and attach it under `this.group` so
   *  it moves with the beam focus. Tuned via /insects → 'magnifying-glass'. */
  private async loadModel(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync(_assetUrl(MODEL_SLOT, MODEL_URL_DEFAULT));
      const tune = readTune(TUNE_ID, TUNE_DEFAULTS);
      const src = gltf.scene;
      src.rotation.set(tune.rotX, tune.rotY, tune.rotZ);
      src.scale.setScalar(tune.scale);
      src.updateMatrixWorld(true);
      // Centre the model horizontally, offset by tune.offY vertically.
      const bb = new THREE.Box3().setFromObject(src);
      const c = new THREE.Vector3(); bb.getCenter(c);
      src.position.set(-c.x, -c.y + tune.offY, -c.z);
      src.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.castShadow = true;
        m.receiveShadow = false;
      });
      const root = new THREE.Group();
      root.name = 'magnifying-glass-model';
      root.add(src);
      this.modelRoot = root;
      this.group.add(root);
    } catch (err) {
      console.warn('[MagnifyingGlass] model load failed:', err);
    }
  }

  get isActive(): boolean {
    return this.active;
  }

  attach(dom: HTMLCanvasElement, camera: THREE.PerspectiveCamera): void {
    this.dom = dom;
    this.camera = camera;
    dom.addEventListener('pointermove', this.onPointerMove);
    dom.addEventListener('pointerleave', this.onPointerLeave);
    window.addEventListener('keydown', this.onKeyDown);
  }

  dispose(): void {
    if (this.dom) {
      this.dom.removeEventListener('pointermove', this.onPointerMove);
      this.dom.removeEventListener('pointerleave', this.onPointerLeave);
      if (this.active) this.dom.style.cursor = this.prevCursor;
    }
    window.removeEventListener('keydown', this.onKeyDown);
    this.dom = null;
    this.camera = null;
  }

  update(
    dt: number,
    damage: (x: number, z: number, radius: number, dps: number, dt: number) => number,
    scatter: (x: number, z: number, radius: number, force?: number) => void,
  ): void {
    if (!this.active) {
      this.group.visible = false;
      return;
    }

    if (!this.hasCursor || !this.camera) {
      this.group.visible = false;
      return;
    }

    // Raycast against terrain to find the beam focus.
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const hits = this.raycaster.intersectObject(this.terrain.mesh, false);
    if (hits.length === 0) {
      this.group.visible = false;
      return;
    }
    const p = hits[0].point;
    this.hitX = p.x;
    this.hitY = p.y;
    this.hitZ = p.z;

    this.group.visible = true;
    this.outerDisc.position.set(this.hitX, this.hitY + 0.01, this.hitZ);
    this.innerDisc.position.set(this.hitX, this.hitY + 0.012, this.hitZ);
    if (this.modelRoot) {
      this.modelRoot.position.set(this.hitX, this.hitY + HOVER_HEIGHT, this.hitZ);
    }

    damage(this.hitX, this.hitZ, 0.9, 2.2, dt);
    scatter(this.hitX, this.hitZ, 2.5, 0.6);
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.dom) return;
    const rect = this.dom.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.hasCursor = true;
  };

  private onPointerLeave = (): void => {
    this.hasCursor = false;
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    const target = e.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
    }
    if (e.key === 'm' || e.key === 'M') {
      this.setActive(!this.active);
    }
  };

  private setActive(on: boolean): void {
    if (on === this.active) return;
    this.active = on;
    if (this.dom) {
      if (on) {
        this.prevCursor = this.dom.style.cursor;
        this.dom.style.cursor = 'crosshair';
      } else {
        this.dom.style.cursor = this.prevCursor;
      }
    }
    this.group.visible = on && this.hasCursor;
  }
}
